import {
  useGetSalaryStructureQuery,
  useUpdateSalaryStructureMutation,
  type SalaryStructureData,
} from '@/app/features/employee/emp-payroll';
import WithLoader from '@/components/WithLoader';
import useAuth from '@/hooks/useAuth';
import { CirclePlusIcon, TrashIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { formatDate } from '@/utils';
import { formatToSnakeCase, snakeToCamel } from '@/utils/string.utils';
import { Button, DatePicker, Form, Input, InputNumber } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Fragment } from 'react';
import CalendarIcon from '../../../Dashboard/_components/Summery/_icons/CalendarIcon';
import PanelCard from './PanelCard';
import {
  CONTROL,
  DATE_DISPLAY,
  disabledBeforeSaved,
  type ConfigPanelProps,
} from './salary-grade.types';
import { useConfigPanel } from './useConfigPanel';

type ComponentRow = { name?: string; percentage?: number };
type FormValues = { components: ComponentRow[]; effectiveDate: Dayjs };

/** Components have to describe the whole pay, no more and no less. */
const REQUIRED_TOTAL = 100;

const sumOf = (rows: ComponentRow[] = []) =>
  rows.reduce((total, row) => total + Number(row?.percentage || 0), 0);

const toValues = (data: SalaryStructureData): FormValues => ({
  components:
    data.salary_structures?.map((item) => ({
      name: snakeToCamel(item?.component_name),
      // The API can hand these back as strings.
      percentage: Number(item?.component_percentage ?? 0),
    })) || [],
  effectiveDate: dayjs(data.salary_structure_effective_date),
});

/** A rule about the rows as a whole, which antd field rules cannot express. */
const validateTotal = ({ components }: FormValues) => {
  if (!components?.length) return 'add at least one component';
  const total = sumOf(components);
  return total === REQUIRED_TOTAL
    ? null
    : `components add up to ${total}%, not 100%`;
};

/**
 * Short messages on purpose: they render under a narrow column, so anything
 * longer than a couple of words would spill past the input.
 */
const percentageValidator = (_: unknown, value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return Promise.resolve();
  }
  const amount = Number(value);
  if (isNaN(amount)) return Promise.reject(new Error('Invalid'));
  if (amount <= 0) return Promise.reject(new Error('Min 0.01'));
  if (amount > REQUIRED_TOTAL) return Promise.reject(new Error('Max 100'));
  return Promise.resolve();
};

/**
 * Name grows, percentage is fixed, and the delete column only exists while
 * editing. The template has to match the cells actually rendered, otherwise the
 * rows wrap into the wrong columns.
 */
const rowGrid = (isEditing: boolean) =>
  cn(
    'grid gap-3 items-start',
    isEditing
      ? 'grid-cols-[minmax(0,1fr)_110px_auto]'
      : 'grid-cols-[minmax(0,1fr)_110px]',
  );

const ManageSalaryStructure = ({
  payType,
  isEditing,
  ref,
}: ConfigPanelProps) => {
  const [form] = Form.useForm<FormValues>();
  const { organizationId, profileInfo } = useAuth();
  const isDaily = payType === 'DAILY';

  const { data, isLoading } = useGetSalaryStructureQuery(payType);
  const [updateSalaryStructure] = useUpdateSalaryStructureMutation();

  const components = Form.useWatch('components', form) || [];
  const total = sumOf(components);
  // Stay neutral while the panel is still empty, e.g. during the first load.
  const showTotalError = total !== REQUIRED_TOTAL && components.length > 0;

  useConfigPanel(ref, {
    form,
    data: data?.data,
    toValues,
    isEditing,
    errorMessage: 'Salary structure update failed',
    extraValidation: validateTotal,
    submit: (values) =>
      updateSalaryStructure({
        organizationId,
        tenantId: profileInfo?.tenant_id,
        payBasis: payType,
        salaryStructures: values.components.map((row) => ({
          componentName: formatToSnakeCase(row?.name || ''),
          componentPercentage: Number(row?.percentage || 0),
        })),
        salaryStructureEffectiveDate: formatDate(
          values.effectiveDate,
          'YYYY-MM-DD',
        ),
      }).unwrap(),
  });

  return (
    <PanelCard title="Salary Structure">
      <WithLoader isLoading={isLoading} className="rounded-lg">
        <Form
          form={form}
          size="middle"
          layout="vertical"
          disabled={!isEditing || isLoading}
          className="custom-form label-gap compact-error space-y-4"
        >
          {/* Header and rows share one grid, so the columns always line up */}
          <Form.List name="components">
            {(fields, { add, remove }) => (
              <div className={rowGrid(isEditing)}>
                <span className="text-xs text-white/40">Component</span>
                <span className="text-xs text-white/40">Percentage</span>
                {isEditing && <span />}

                {fields.map(({ key, name }) => (
                  <Fragment key={key}>
                    <Form.Item
                      name={[name, 'name']}
                      className="!mb-0"
                      rules={[{ required: true, message: 'Required' }]}
                    >
                      <Input
                        autoComplete="off"
                        placeholder="Basic"
                        className="!bg-transparent !border-theme-slate-700"
                      />
                    </Form.Item>

                    <Form.Item
                      name={[name, 'percentage']}
                      className="disabled-bg-slate-900 !mb-0"
                      rules={[
                        { required: true, message: 'Required' },
                        { validator: percentageValidator },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        suffix="%"
                        precision={2}
                        controls={false}
                        placeholder="00"
                        className="!border-transparent w-full"
                      />
                    </Form.Item>

                    {isEditing && (
                      <Button
                        type="text"
                        onClick={() => remove(name)}
                        icon={<TrashIcon size={18} />}
                        classNames={{ icon: 'flex items-center justify-center' }}
                        className={cn(
                          CONTROL,
                          '!w-[38px] !px-0 border border-theme-gray-800 !text-theme-error-500',
                        )}
                      />
                    )}
                  </Fragment>
                ))}

                {isEditing && (
                  <button
                    type="button"
                    onClick={() => add()}
                    className={cn(
                      CONTROL,
                      'col-span-full w-full border border-dashed border-theme-primary/60',
                      'flex items-center justify-center gap-2 text-sm text-theme-primary',
                      'hover:bg-theme-primary/10',
                    )}
                  >
                    <CirclePlusIcon size={18} />
                    Add component
                  </button>
                )}
              </div>
            )}
          </Form.List>

          <div
            className={cn(
              'flex items-center justify-between h-[46px] px-4 rounded-md border bg-theme-slate-900',
              showTotalError
                ? 'border-theme-error-500'
                : 'border-theme-gray-700',
            )}
          >
            <span className="text-sm text-white/55">
              {isDaily ? 'Total daily breakdown' : 'Total breakdown'}
            </span>
            <span
              className={cn(
                'text-base font-semibold',
                showTotalError ? 'text-theme-error-500' : 'text-theme-primary',
              )}
            >
              {total}%
            </span>
          </div>

          <Form.Item
            name="effectiveDate"
            label="Effective Date"
            className="disabled-bg-slate-900 !mb-0 max-w-[200px]"
            rules={[{ required: true, message: 'Select an effective date' }]}
          >
            <DatePicker
              format={DATE_DISPLAY}
              placeholder="Select Date"
              className="!border-transparent w-full"
              suffixIcon={<CalendarIcon className="text-lg text-white" />}
              disabledDate={disabledBeforeSaved(
                data?.data?.salary_structure_effective_date,
              )}
            />
          </Form.Item>
        </Form>
      </WithLoader>
    </PanelCard>
  );
};

export default ManageSalaryStructure;
